import Subscriptions from "lib/subscriptions";
import { fileDownloadFormats } from "lib/constants";
import { cloneDeep } from "lib";
import {
  SCALE_OPTIONS,
  getAvailableScaleOptions
} from "views/components/Editor/sidebar/tabs/downloads/scaleOptions";

export {
  DEFAULT_SCALE,
  isEligibleForDownscale,
  getAvailableScaleOptions
} from "views/components/Editor/sidebar/tabs/downloads/scaleOptions";

export const applyDisabledValues = (
  fileTypes,
  downloadOptions,
  subscription,
  exportToMyImages,
  hidePDFDownload
) => {
  if (typeof fileTypes !== "object") return [];

  const updatedFileTypes = cloneDeep(fileTypes);
  if (hidePDFDownload) delete updatedFileTypes.PDF;

  return Object.values(updatedFileTypes).map(option => {
    const isDownloadableOnPlan = subscription.canDownloadType(option.value);
    const isAvailableForDesign = downloadOptions.includes(option.value);
    const isDisabled =
      !isAvailableForDesign ||
      !isDownloadableOnPlan ||
      (exportToMyImages && (option.value === "PDF" || option.value === "PPT"));
    return Object.assign({}, option, {
      isDownloadableOnPlan,
      isAvailableForDesign,
      isDisabled
    });
  });
};

export const allowedFormats = ({
  design,
  designTemplate,
  numberOfPages,
  isDesignAnimated
}) => {
  const allowedFormats = ["JPG", "PNG"];
  const isImageType = designTemplate && designTemplate.templateType === "image";
  const isEBookCover =
    isDesignAnimated && isDesignAnimated.templateCode === "ebooks_covers";
  const isCustomPxDesign =
    design &&
    design.templateCode === "custom" &&
    design.measurementUnits === "px";
  const isLessThanMaxPixels =
    design && design.height * design.width <= 1920 * 1080;

  if (!isImageType || isEBookCover) {
    allowedFormats.push("PDF");
  }

  const isPxDesign = design && design.measurementUnits === "px";
  if (isImageType || isPxDesign) {
    allowedFormats.push("PPT");
  }

  if (isImageType || (isCustomPxDesign && isLessThanMaxPixels)) {
    if (numberOfPages > 1) {
      allowedFormats.push("GIF");
    }
    if ((!isDesignAnimated && numberOfPages > 1) || isDesignAnimated) {
      allowedFormats.push("MP4");
    }
  }

  return allowedFormats;
};

export const GIF_SPEEDS = [
  { key: "extra_slow", label: "Extra Slow" },
  { key: "slow", label: "Slow" },
  { key: "medium", label: "Medium" },
  { key: "fast", label: "Fast" },
  { key: "extra_fast", label: "Extra Fast" }
];

// MP4 renderer only supports the original three speeds — must stay decoupled
// from GIF_SPEEDS so future GIF-only additions don't leak into MP4.
export const MP4_SPEEDS = [
  { key: "slow", label: "Slow" },
  { key: "medium", label: "Medium" },
  { key: "fast", label: "Fast" }
];

// Labels match the editor sidebar's qualityOptions.shortLabel form so both
// surfaces render the same selected text and option list.
export const PDF_QUALITIES = [
  { key: "print", label: "Print" },
  { key: "web", label: "Standard" },
  { key: "web-small", label: "Smaller" }
];

export const JPG_QUALITIES = [
  { key: "print", label: "Print" },
  { key: "web", label: "Web" }
];

// Generic raster scale list (popout dropdown shape). Mirrors SCALE_OPTIONS from
// scaleOptions.ts but in {key, label} shape for DownloadFooterDropdown.
export const RASTER_SCALES = SCALE_OPTIONS.map(option => ({
  key: option.value,
  label: option.title
}));

// Format-aware scale list — applies eligibility + format rules from scaleOptions.
export const getRasterScalesForFormat = (designData, format) =>
  getAvailableScaleOptions(designData, format).map(option => ({
    key: option.value,
    label: option.title
  }));

export const formatDropdownProperties = {
  PDF: {
    title: "Select quality",
    options: PDF_QUALITIES
  },
  JPG: {
    title: "Select quality",
    options: JPG_QUALITIES
  },
  GIF: {
    title: "Select speed",
    options: GIF_SPEEDS
  },
  MP4: {
    title: "Select speed",
    options: MP4_SPEEDS
  }
};

export const SCALE_DROPDOWN_PROPERTIES = {
  title: "Select scale",
  options: RASTER_SCALES
};

export const isDesignTemplatePrintable = designTemplate =>
  designTemplate &&
  (designTemplate.templateType !== "image" ||
    designTemplate.templateCode === "ebooks_covers");

export const getDefaultDownloadFormat = ({
  planCode,
  downloadOptions,
  design,
  designTemplate
}) => {
  // BASIC PLAN
  if (planCode === Subscriptions.BASIC.code) {
    // BASIC should only have JPG option, if that isn't available return null
    if (downloadOptions.includes(fileDownloadFormats.JPG)) {
      return fileDownloadFormats.JPG;
    }
    return null;
  }

  // PLUS AND EDGE PLAN
  if (isDesignTemplatePrintable(designTemplate)) {
    // printable designs should default to PDF
    if (downloadOptions.includes(fileDownloadFormats.PDF)) {
      return fileDownloadFormats.PDF;
    }
    return null;
  }

  // not a printable design
  if (design.isAnimated) {
    // animated designs should default to mp4
    if (downloadOptions.includes(fileDownloadFormats.MP4)) {
      return fileDownloadFormats.MP4;
    }
    return null;
  }

  // anything else should be defaulted to PNG
  if (downloadOptions.includes(fileDownloadFormats.PNG)) {
    return fileDownloadFormats.PNG;
  }
  return null;
};
